import { useEffect, useState } from 'react';
import api from '../lib/api';

const TYPE_ICONS = {
  meeting: { icon: '📅', label: 'Meeting', color: '#E6F1FB', text: '#0C447C' },
  call: { icon: '📞', label: 'Call', color: '#EAF3DE', text: '#27500A' },
  virtual_call: { icon: '💻', label: 'Virtual Call', color: '#FAEEDA', text: '#633806' },
  note: { icon: '📝', label: 'Note', color: '#F1EFE8', text: '#444441' },
  email: { icon: '✉️', label: 'Email', color: '#FBEAF0', text: '#72243E' },
};

export default function ActivityPanel({ contactId, companyId, dealId }) {
  const [events, setEvents] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventType, setEventType] = useState('call');
  const [eventTitle, setEventTitle] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [eventNotes, setEventNotes] = useState('');
  const [savingEvent, setSavingEvent] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editForm, setEditForm] = useState({});

  const notesKey = dealId ? `deal_${dealId}` : companyId ? `company_${companyId}` : `contact_${contactId}`;

  const load = async () => {
    setLoading(true);
    try {
      const promises = [];
      if (dealId) promises.push(api.get(`/events/linked/deal/${dealId}`));
      else if (companyId) promises.push(api.get(`/events/linked/company/${companyId}`));
      else if (contactId) promises.push(api.get(`/events/linked/contact/${contactId}`));

      const results = await Promise.all(promises);
      setEvents(results[0]?.data?.events || []);

      const stored = localStorage.getItem(`notes_${notesKey}`);
      setNotes(stored ? JSON.parse(stored) : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [contactId, companyId, dealId]);

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    const newNote = {
      id: Date.now().toString(),
      type: 'note',
      _type: 'note',
      title: noteText,
      created_at: new Date().toISOString(),
    };
    const updated = [newNote, ...notes];
    localStorage.setItem(`notes_${notesKey}`, JSON.stringify(updated));
    setNotes(updated);
    setNoteText('');
    setShowNoteForm(false);
    setSavingNote(false);
  };

  const handleDeleteNote = (noteId) => {
    const updated = notes.filter(n => n.id !== noteId);
    localStorage.setItem(`notes_${notesKey}`, JSON.stringify(updated));
    setNotes(updated);
    setExpandedId(null);
  };

  const handleEditNote = (note) => {
    const updated = notes.map(n => n.id === note.id ? { ...n, title: editForm.title } : n);
    localStorage.setItem(`notes_${notesKey}`, JSON.stringify(updated));
    setNotes(updated);
    setEditingEvent(null);
    setExpandedId(note.id);
  };

  const handleAddEvent = async () => {
    if (!eventTitle.trim()) return alert('Title is required.');
    if (!eventTime) return alert('Date and time is required.');
    setSavingEvent(true);
    try {
      await api.post('/events', {
        title: eventTitle,
        type: eventType,
        start_time: eventTime,
        notes: eventNotes,
        contact_id: contactId || null,
        company_id: companyId || null,
        deal_id: dealId || null,
      });
      setEventTitle('');
      setEventTime('');
      setEventNotes('');
      setShowEventForm(false);
      load();
    } catch (err) {
      alert('Failed to log event.');
    } finally {
      setSavingEvent(false);
    }
  };

  const handleUpdateEvent = async (eventId) => {
    try {
      await api.patch(`/events/${eventId}`, editForm);
      setEditingEvent(null);
      load();
    } catch (err) {
      alert('Failed to update event.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Delete this event?')) return;
    await api.delete(`/events/${eventId}`);
    setExpandedId(null);
    load();
  };

  const handleStatusToggle = async (event) => {
    const newStatus = event.status === 'open' ? 'closed' : 'open';
    await api.patch(`/events/${event.id}`, { status: newStatus });
    load();
  };

  const allItems = [
    ...events.map(e => ({ ...e, _type: e.type })),
    ...notes.map(n => ({ ...n, _type: 'note' })),
  ].sort((a, b) => new Date(b.start_time || b.created_at) - new Date(a.start_time || a.created_at));

  const filtered = activeTab === 'all' ? allItems
    : activeTab === 'events' ? allItems.filter(i => ['meeting','call','virtual_call'].includes(i._type))
    : allItems.filter(i => i._type === 'note');

  const upcoming = filtered.filter(i => i._type !== 'note' && new Date(i.start_time) > new Date() && i.status === 'open');
  const past = filtered.filter(i => i._type === 'note' || new Date(i.start_time || i.created_at) <= new Date() || i.status === 'closed');

  const typeInfo = (type) => TYPE_ICONS[type] || TYPE_ICONS.note;

  return (
    <div style={{ display:'flex', flexDirection:'column' }}>
      {/* Quick action buttons */}
      <div style={{ display:'flex', gap:8, marginBottom:12 }}>
        <button className="btn" style={{ fontSize:12, height:28, flex:1 }}
          onClick={() => { setShowEventForm(!showEventForm); setEventType('call'); setShowNoteForm(false); }}>
          📞 Log call
        </button>
        <button className="btn" style={{ fontSize:12, height:28, flex:1 }}
          onClick={() => { setShowNoteForm(!showNoteForm); setShowEventForm(false); }}>
          📝 Add note
        </button>
        <button className="btn" style={{ fontSize:12, height:28, flex:1 }}
          onClick={() => { setShowEventForm(!showEventForm); setEventType('meeting'); setShowNoteForm(false); }}>
          📅 Log meeting
        </button>
      </div>

      {/* Log Event Form */}
      {showEventForm && (
        <div style={{ background:'var(--bg-secondary)', borderRadius:'var(--radius)', padding:12, marginBottom:12 }}>
          <div style={{ marginBottom:8 }}>
            <label className="form-label">Type</label>
            <select className="form-input" value={eventType} onChange={e => setEventType(e.target.value)}>
              <option value="call">Call</option>
              <option value="meeting">Meeting</option>
              <option value="virtual_call">Virtual call</option>
            </select>
          </div>
          <div style={{ marginBottom:8 }}>
            <label className="form-label">Title *</label>
            <input className="form-input" value={eventTitle}
              onChange={e => setEventTitle(e.target.value)}
              placeholder="e.g. Follow up call with buyer" />
          </div>
          <div style={{ marginBottom:8 }}>
            <label className="form-label">Date & time *</label>
            <input className="form-input" type="datetime-local" value={eventTime}
              onChange={e => setEventTime(e.target.value)} />
          </div>
          <div style={{ marginBottom:8 }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" value={eventNotes}
              onChange={e => setEventNotes(e.target.value)}
              placeholder="What was discussed?"
              style={{ height:60, resize:'vertical' }} />
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary" style={{ fontSize:12, height:28 }}
              onClick={handleAddEvent} disabled={savingEvent}>
              {savingEvent ? 'Saving…' : 'Save'}
            </button>
            <button className="btn" style={{ fontSize:12, height:28 }}
              onClick={() => setShowEventForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Add Note Form */}
      {showNoteForm && (
        <div style={{ background:'var(--bg-secondary)', borderRadius:'var(--radius)', padding:12, marginBottom:12 }}>
          <label className="form-label">Note</label>
          <textarea className="form-input" value={noteText}
            onChange={e => setNoteText(e.target.value)}
            placeholder="Add a note…"
            style={{ height:80, resize:'vertical', marginBottom:8 }} />
          <div style={{ display:'flex', gap:8 }}>
            <button className="btn btn-primary" style={{ fontSize:12, height:28 }}
              onClick={handleAddNote} disabled={savingNote}>
              {savingNote ? 'Saving…' : 'Save note'}
            </button>
            <button className="btn" style={{ fontSize:12, height:28 }}
              onClick={() => setShowNoteForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:12, borderBottom:'0.5px solid var(--border)' }}>
        {['all','events','notes'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding:'6px 12px', fontSize:12, border:'none', cursor:'pointer',
                     background:'transparent',
                     borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                     color: activeTab === tab ? 'var(--accent)' : 'var(--text-secondary)',
                     fontWeight: activeTab === tab ? 500 : 400, textTransform:'capitalize' }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Upcoming */}
      {upcoming.length > 0 && (
        <div style={{ marginBottom:8 }}>
          <div style={{ fontSize:11, fontWeight:600, color:'var(--text-tertiary)',
                        textTransform:'uppercase', letterSpacing:'.04em', marginBottom:6 }}>
            Upcoming
          </div>
          {upcoming.map(item => (
            <ActivityItem
              key={item.id} item={item}
              expanded={expandedId === item.id}
              onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
              editing={editingEvent === item.id}
              editForm={editForm}
              onEditStart={() => { setEditingEvent(item.id); setEditForm({ title: item.title, notes: item.notes || '', status: item.status }); }}
              onEditSave={() => item._type === 'note' ? handleEditNote(item) : handleUpdateEvent(item.id)}
              onEditCancel={() => setEditingEvent(null)}
              onEditFormChange={setEditForm}
              onDelete={() => item._type === 'note' ? handleDeleteNote(item.id) : handleDeleteEvent(item.id)}
              onStatusToggle={() => handleStatusToggle(item)}
            />
          ))}
        </div>
      )}

      {/* Past / all */}
      {loading
        ? <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'12px 0' }}>Loading…</div>
        : past.length === 0 && upcoming.length === 0
          ? <div style={{ fontSize:13, color:'var(--text-tertiary)', padding:'12px 0', textAlign:'center' }}>
              No activity yet. Log a call, meeting, or note above.
            </div>
          : past.map(item => (
              <ActivityItem
                key={item.id} item={item}
                expanded={expandedId === item.id}
                onToggle={() => setExpandedId(expandedId === item.id ? null : item.id)}
                editing={editingEvent === item.id}
                editForm={editForm}
                onEditStart={() => { setEditingEvent(item.id); setEditForm({ title: item.title, notes: item.notes || '', status: item.status }); }}
                onEditSave={() => item._type === 'note' ? handleEditNote(item) : handleUpdateEvent(item.id)}
                onEditCancel={() => setEditingEvent(null)}
                onEditFormChange={setEditForm}
                onDelete={() => item._type === 'note' ? handleDeleteNote(item.id) : handleDeleteEvent(item.id)}
                onStatusToggle={() => item._type !== 'note' && handleStatusToggle(item)}
              />
            ))
      }
    </div>
  );
}

function ActivityItem({ item, expanded, onToggle, editing, editForm, onEditStart, onEditSave, onEditCancel, onEditFormChange, onDelete, onStatusToggle }) {
  const typeInfo = TYPE_ICONS[item._type] || TYPE_ICONS.note;
  const date = new Date(item.start_time || item.created_at);
  const isFuture = item._type !== 'note' && date > new Date() && item.status === 'open';

  return (
    <div style={{ borderBottom:'0.5px solid var(--border)', marginBottom:2 }}>
      {/* Header row — always visible */}
      <div
        onClick={onToggle}
        style={{ display:'flex', gap:10, padding:'10px 0', alignItems:'flex-start', cursor:'pointer' }}
      >
        <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                      background: typeInfo.color, display:'flex', alignItems:'center',
                      justifyContent:'center', fontSize:13 }}>
          {typeInfo.icon}
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ fontSize:13, fontWeight:500, color:'var(--text-primary)',
                        textDecoration: item.status === 'closed' ? 'line-through' : 'none',
                        opacity: item.status === 'closed' ? 0.7 : 1 }}>
            {item.title}
          </div>
          <div style={{ fontSize:11, color:'var(--text-tertiary)', marginTop:2, display:'flex', gap:8, flexWrap:'wrap' }}>
            <span>{typeInfo.label}</span>
            <span>·</span>
            <span>{date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })}</span>
            {item.start_time && <span>{date.toLocaleTimeString([], { hour:'2-digit', minute:'2-digit' })}</span>}
            {item.owner_name && <><span>·</span><span>{item.owner_name}</span></>}
            {isFuture && <span className="badge badge-blue" style={{ fontSize:10, padding:'1px 6px' }}>Upcoming</span>}
            {item.status === 'closed' && <span className="badge badge-gray" style={{ fontSize:10, padding:'1px 6px' }}>Closed</span>}
          </div>
        </div>
        <div style={{ color:'var(--text-tertiary)', fontSize:12, flexShrink:0 }}>
          {expanded ? '▲' : '▼'}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding:'0 0 12px 38px' }}>
          {editing ? (
            <div>
              <div style={{ marginBottom:8 }}>
                <label className="form-label">Title</label>
                <input className="form-input" value={editForm.title || ''}
                  onChange={e => onEditFormChange({...editForm, title: e.target.value})} />
              </div>
              <div style={{ marginBottom:8 }}>
                <label className="form-label">Notes</label>
                <textarea className="form-input" value={editForm.notes || ''}
                  onChange={e => onEditFormChange({...editForm, notes: e.target.value})}
                  style={{ height:80, resize:'vertical' }}
                  placeholder="Add notes here…" />
              </div>
              {item._type !== 'note' && (
                <div style={{ marginBottom:8 }}>
                  <label className="form-label">Status</label>
                  <select className="form-input" value={editForm.status || 'open'}
                    onChange={e => onEditFormChange({...editForm, status: e.target.value})}>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
              )}
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn btn-primary" style={{ fontSize:12, height:28 }} onClick={onEditSave}>Save</button>
                <button className="btn" style={{ fontSize:12, height:28 }} onClick={onEditCancel}>Cancel</button>
              </div>
            </div>
          ) : (
            <div>
              {item.notes
                ? <div style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6,
                                 background:'var(--bg-secondary)', borderRadius:'var(--radius)',
                                 padding:'10px 12px', marginBottom:10, whiteSpace:'pre-wrap' }}>
                    {item.notes}
                  </div>
                : <div style={{ fontSize:13, color:'var(--text-tertiary)', marginBottom:10, fontStyle:'italic' }}>
                    No notes added.
                  </div>
              }
              <div style={{ display:'flex', gap:8 }}>
                <button className="btn" style={{ fontSize:12, height:28 }} onClick={onEditStart}>Edit</button>
                {item._type !== 'note' && (
                  <button className="btn" style={{ fontSize:12, height:28 }} onClick={onStatusToggle}>
                    Mark {item.status === 'open' ? 'closed' : 'open'}
                  </button>
                )}
                <button className="btn" style={{ fontSize:12, height:28, color:'var(--danger)' }} onClick={onDelete}>
                  Delete
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}